import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Grid, Typography, Container } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useApiStore } from '@core/store';
import { eventBus, EventTypes } from '@core/pubsub/EventBus';
import { NewPageTitleBar } from '@/components';
import { getMenuData } from '@core/data/menuData';
import { FCSecurePage, useRegisterRefresh, useShopPersistence } from '@/components/shared/navigation/securePage.ts';
import ShopSelector from '@/components/selectors/ShopSelector';

import TrolleyShelterCountCapacity from '@/components/widgets/TrolleyShelterCountCapacity';
import TrolleySheltersCountCapacity from '@/components/widgets/TrolleySheltersCountCapacity';

const TrolleyShelterOverview: FCSecurePage = () => {
    const { t } = useTranslation();
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { shopIds, trolleyShelterIds, loadTrolleyShelterIds } = useApiStore();

    const reloadMinutes = 5;
    const widgetColClasses = { xl: 4, lg: 4, md: 4, sm: 6, xs: 12 };

    useEffect(() => {
        loadTrolleyShelterIds();
    }, []);

    const selectedShop = shopIds.find((s) => s.id === selectedShopIds[0]) ?? null;

    const sheltersForShop = trolleyShelterIds.filter(
        (ts) => ts.shopId === selectedShopIds[0] && !ts.disabled && !ts.deleteDate
    );

    useEffect(() => {
        if (selectedShopIds[0] && sheltersForShop.length > 0) {
            setLastUpdate(DateTime.now());
        }
    }, [selectedShopIds[0], sheltersForShop.length]);

    const reloadWidgets = useCallback(async () => {
        try {
            eventBus.emit(EventTypes.WIDGETS_RELOAD);
            setLastUpdate(DateTime.now());
        } catch (error) {
            console.error('Error reloading widgets:', error);
        }
    }, []);

    useRegisterRefresh(reloadWidgets, !selectedShopIds[0]);

    const startReloadTimer = useCallback(() => {
        if (reloadTimerRef.current) {
            clearTimeout(reloadTimerRef.current);
        }
        reloadTimerRef.current = setTimeout(
            () => {
                reloadWidgets();
                startReloadTimer();
            },
            reloadMinutes * 60 * 1000
        );
    }, [reloadMinutes, reloadWidgets]);

    useEffect(() => {
        startReloadTimer();
        return () => {
            if (reloadTimerRef.current) {
                clearTimeout(reloadTimerRef.current);
            }
        };
    }, [startReloadTimer]);

    return (
        <Box sx={{ width: '100%', bgcolor: 'background.default' }}>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-cast zmdi-hc-rotate-180" style={{ fontSize: '16px' }} />}
                title={`${t('trolleyShelterOverview')}`}
                menus={getMenuData()}
                iconSize="18px"
                currentRoute={{ path: '/statistics/rfid/overview' }}
                t={t}
                lastUpdate={lastUpdate}
                hasAutoRefresh={true}
            />

            <Container maxWidth={false} sx={{ py: 1 }}>
                <Box sx={{ mb: 5 }}>
                    <ShopSelector
                        value={selectedShopIds[0] ?? null}
                        onChange={(id) => setSelectedShopIds(id ? [id] : [])}
                        label={t('shop')}
                    />
                </Box>

                {!selectedShopIds[0] && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('selectAShop')}
                        </Typography>
                    </Box>
                )}

                {selectedShopIds[0] && selectedShop && (
                    <>
                        {sheltersForShop.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" color="text.secondary">
                                    {t('noData')}
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="body1" sx={{ pb: 0 }}>
                                        {t('shopTrolleyShelters')}
                                    </Typography>
                                </Grid>

                                <Grid item {...widgetColClasses}>
                                    <TrolleySheltersCountCapacity
                                        shopIdName={selectedShop}
                                        heading={t('trolleyShelterOverview')}
                                    />
                                </Grid>

                                {sheltersForShop.map((trolleyShelterId, idx) => (
                                    <Grid item {...widgetColClasses} key={`ts-${selectedShopIds[0]}-${idx}`}>
                                        <TrolleyShelterCountCapacity
                                            shopIdName={selectedShop}
                                            heading={trolleyShelterId.id}
                                            deviceId={trolleyShelterId.deviceId}
                                            moduleId={trolleyShelterId.id}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                )}
            </Container>
        </Box>
    );
};

TrolleyShelterOverview.displayName = 'TrolleyShelterOverview';
export default TrolleyShelterOverview;
